// Static manifest built from every Featured Guide's places whose Photo/
// Photo_m already points at our own andiamotravelapp.com/Images hosting —
// see memory: project_destinations_first_redesign_idea (2026-08-27). Reusing
// these (photo + cached Wikipedia info) for newly-searched places that match
// by country/city/name avoids a live Places/Wikimedia/Custom-Search call
// entirely, at zero per-view cost since it's static hosting we already pay
// for. Served from public/ (not imported as a JS module) because the file
// is ~3.7MB with the info text included — bundling it would bloat every
// page load; fetched once here and cached in memory instead.
const MANIFEST_URL = "/Data/FeaturedPlaceImages.json";

let manifestPromise = null;
let countryKeyByLower = null;

function loadManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch(MANIFEST_URL)
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        countryKeyByLower = {};
        Object.keys(data).forEach((k) => {
          countryKeyByLower[k.toLowerCase()] = k;
        });
        return data;
      })
      .catch(() => {
        countryKeyByLower = {};
        return {};
      });
  }
  return manifestPromise;
}

function findCityBucket(manifest, country, city) {
  if (!country || !city) return null;
  const realCountryKey = countryKeyByLower?.[country.toLowerCase()];
  const countryBucket = realCountryKey ? manifest[realCountryKey] : null;
  if (!countryBucket) return null;
  const cityLower = city.toLowerCase();
  const realCityKey = Object.keys(countryBucket).find((k) => k.toLowerCase() === cityLower);
  return realCityKey ? countryBucket[realCityKey] : null;
}

// Words too generic to count as a real signal on their own (mostly
// place-type nouns and multi-language articles/prepositions) — stripped
// before comparing, along with any trailing ", <disambiguator>" suffix (a
// lot of manifest entries are named "X, Florence" to disambiguate from
// same-named places elsewhere). Without this, "Basilica di Santa Croce"
// (Google's official name) vs. the manifest's "Santa Croce, Florence" share
// zero tokens in common once "basilica"/"di" and the city suffix are in the
// set — neither tokenSubstringMatch direction ever matched, so real matches
// were silently missed for anything not named identically.
const STOPWORDS = new Set([
  "the", "of", "a", "an",
  "di", "da", "de", "del", "della", "dei", "degli", "delle", "la", "le", "il", "lo",
  "basilica", "chiesa", "church", "cathedral", "cattedrale", "duomo", "piazza", "palazzo", "palace",
  "museum", "museo", "gallery", "galleria", "garden", "gardens", "giardino",
]);

function normalizeTokens(name) {
  const withoutSuffix = (name || "").split(",")[0];
  return new Set(
    withoutSuffix
      .toLowerCase()
      .replace(/['’]/g, "")
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length > 1 && !STOPWORDS.has(t))
  );
}

function overlapScore(aTokens, bTokens) {
  if (aTokens.size === 0 || bTokens.size === 0) return 0;
  let shared = 0;
  aTokens.forEach((t) => {
    if (bTokens.has(t)) shared++;
  });
  return shared / Math.min(aTokens.size, bTokens.size);
}

// Returns the best-matching place in that country/city bucket for `name`
// (fuzzy, via a stopword-stripped token-overlap score — not exact-string or
// strict-substring matching, since real-world names for the same place
// rarely agree on word order/inclusion, e.g. official vs. common names,
// language differences, or a disambiguating city suffix), or null if there's
// no manifest coverage for that country/city, or nothing scores highly
// enough to trust as the same place.
const MATCH_THRESHOLD = 0.6;

export async function getFeaturedPlaceMatch(country, city, name) {
  if (!country || !city || !name) return null;
  const manifest = await loadManifest();
  const bucket = findCityBucket(manifest, country, city);
  if (!bucket || bucket.length === 0) return null;
  const nameTokens = normalizeTokens(name);
  if (nameTokens.size === 0) return null;
  let best = null;
  let bestScore = 0;
  bucket.forEach((entry) => {
    const score = overlapScore(nameTokens, normalizeTokens(entry.name));
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  });
  return bestScore >= MATCH_THRESHOLD ? best : null;
}

// For a destination with rich-enough manifest coverage, GooglePlaces.
// searchCityAttractions can skip the live Places Text Search call entirely
// and use these entries directly as candidates — real dollar savings (the
// search call itself, not just photos, is Google's remaining billed cost
// here) at the trade-off of no live opening-hours/address/phone/rating for
// these places (manifest entries only ever had name/photo/info/coords, since
// that's all a Featured Guide's place data carries). minCount lets the
// caller decide how much coverage justifies skipping the live search —
// see memory: project_destinations_first_redesign_idea for the coverage
// analysis this threshold was picked from (231/444 manifest cities have 15+
// places).
export async function getFeaturedCityBucket(country, city, minCount) {
  if (!country || !city) return null;
  const manifest = await loadManifest();
  const bucket = findCityBucket(manifest, country, city);
  if (!bucket || bucket.length < minCount) return null;
  return bucket;
}
